import React, { FC } from 'react'
import {
  Box,
  Typography,
  Switch,
  Stack,
  Card,
  Divider,
  Select,
  MenuItem,
  FormControl,
  Tooltip,
} from '@mui/material'
import {
  useSettingQuery,
  useUpdateSettingMutation,
  useUpdateUIConfigMutation,
} from '@/features/auth'
import { Setting } from '@/features/auth/types/setting'
import {
  resolveSoapModelLatest,
  selectableSoapModelMajors,
  soapModelMajorOf,
  type SoapModelMajor,
} from '@/features/auth/utils/soapModelVersion'
import { settingDescriptions } from '@/apps/legacy/constants/settingDescriptions'
import { PortalLayout } from '@/apps/legacy/components/portal/PortalLayout'
import { PortalPageHeader } from '@/apps/legacy/components/portal/PortalPageHeader'
import { colorTokens } from '@/apps/legacy/shared/styles/colorTokens'

export const SoapSettingsPage: FC = () => {
  const { data: customSetting } = useSettingQuery()
  const updateSettingMutation = useUpdateSettingMutation()
  // display_days は UserUIConfig(ui_config) 管理へ移行済。
  const updateUIConfigMutation = useUpdateUIConfigMutation()

  const updateSetting = (key: keyof Setting, value: Setting[keyof Setting]) => {
    updateSettingMutation.mutate({ key, value })
  }
  // TanStack Query が自動的にマウント時にフェッチするため useEffect 不要

  if (!customSetting) {
    return (
      <PortalLayout>
        <Typography>読み込み中...</Typography>
      </PortalLayout>
    )
  }

  // ユーザーには major (v1 / v2 / v3) だけ見せ、選択時は当該 major の最新 patch を自動設定する.
  // v3 は GA。配信されている系統をそのまま出す (selectableSoapModelMajors 参照)
  const modelLatest = resolveSoapModelLatest(
    customSetting.soap_prompt_versions_available
  )
  const soapModelOptions = selectableSoapModelMajors(
    customSetting.soap_prompt_versions_available
  ).map((m) => ({ value: m, label: m }))

  // 軽量モードが生成に効くか。server が model 設定表から導出して配る値をそのまま使う
  // (front で version から推測しない — 版と世代が食い違う状態があるため)。
  const quickModeEffective = customSetting.is_quick_mode_effective ?? true

  return (
    <PortalLayout>
      <PortalPageHeader title="SOAP作成基本設定" />
      <Stack spacing={3}>
        {/* 旧版互換設定セクション */}
        <Card sx={{ p: 3 }}>
          <Typography
            variant="subtitle1"
            sx={{
              color: colorTokens.blue[700],
              mb: 2,
            }}
          >
            {settingDescriptions.legacyCompat.title}
          </Typography>

          <Stack spacing={3}>
            {/* 高速SOAP生成 (transcription_mode) */}
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              spacing={2}
            >
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                  {
                    settingDescriptions.legacyCompat.items.transcription_mode
                      .title
                  }
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {
                    settingDescriptions.legacyCompat.items.transcription_mode
                      .description
                  }
                </Typography>
              </Box>
              <Switch
                color="primary"
                checked={customSetting.transcription_mode === 'streaming'}
                onChange={() =>
                  updateSetting(
                    'transcription_mode',
                    customSetting.transcription_mode === 'streaming'
                      ? 'batch'
                      : 'streaming'
                  )
                }
              />
            </Stack>

            <Divider />

            {/* SOAP生成モデル */}
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              spacing={2}
            >
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                  {
                    settingDescriptions.legacyCompat.items.soap_prompt_version
                      .title
                  }
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {
                    settingDescriptions.legacyCompat.items.soap_prompt_version
                      .description
                  }
                </Typography>
              </Box>
              <FormControl sx={{ minWidth: 120 }}>
                <Select
                  value={soapModelMajorOf(customSetting.soap_prompt_version)}
                  onChange={(e) => {
                    const target = modelLatest[e.target.value as SoapModelMajor]
                    if (target) updateSetting('soap_prompt_version', target)
                  }}
                  size="small"
                  sx={{ backgroundColor: 'background.paper' }}
                  displayEmpty
                >
                  {!soapModelOptions.length && (
                    <MenuItem value="" disabled>
                      読込中
                    </MenuItem>
                  )}
                  {soapModelOptions.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            <Divider />

            {/* 従来の画面 */}
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              spacing={2}
            >
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                  {settingDescriptions.legacyCompat.items.use_legacy_ui.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {
                    settingDescriptions.legacyCompat.items.use_legacy_ui
                      .description
                  }
                </Typography>
              </Box>
              <Switch
                color="primary"
                checked={customSetting.use_legacy_ui ?? false}
                onChange={() =>
                  updateSetting('use_legacy_ui', !customSetting.use_legacy_ui)
                }
              />
            </Stack>
          </Stack>
        </Card>

        {/* 音声認識設定セクション */}
        <Card sx={{ p: 3 }}>
          <Typography
            variant="subtitle1"
            sx={{
              color: colorTokens.blue[700],
              mb: 2,
            }}
          >
            {settingDescriptions.speechRecognition.title}
          </Typography>

          <Stack spacing={3}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              spacing={2}
            >
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                  {
                    settingDescriptions.speechRecognition.items.is_quick_mode
                      .title
                  }
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {
                    settingDescriptions.speechRecognition.items.is_quick_mode
                      .description
                  }
                </Typography>
              </Box>
              {/* 効かない世代では軽量モデルが使われないため、オフ固定の変更不可で見せる。
                  disabled な Switch はホバーを拾わないため span でラップして Tooltip を出す。 */}
              {quickModeEffective ? (
                <Switch
                  color="primary"
                  checked={customSetting.is_quick_mode ?? false}
                  onChange={() =>
                    updateSetting('is_quick_mode', !customSetting.is_quick_mode)
                  }
                />
              ) : (
                <Tooltip title="現在のSOAPモデルでは使用されません">
                  <span>
                    <Switch color="primary" checked={false} disabled />
                  </span>
                </Tooltip>
              )}
            </Stack>

            <Divider />

            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              spacing={2}
            >
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                  {
                    settingDescriptions.speechRecognition.items
                      .is_noise_cancelling.title
                  }
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {
                    settingDescriptions.speechRecognition.items
                      .is_noise_cancelling.description
                  }
                </Typography>
              </Box>
              <Switch
                color="primary"
                checked={customSetting.is_noise_cancelling ?? false}
                onChange={() =>
                  updateSetting(
                    'is_noise_cancelling',
                    !customSetting.is_noise_cancelling
                  )
                }
              />
            </Stack>
          </Stack>
        </Card>

        {/* SOAP出力設定セクション */}
        <Card sx={{ p: 3 }}>
          <Typography
            variant="subtitle1"
            sx={{
              color: colorTokens.blue[700],
              mb: 2,
            }}
          >
            {settingDescriptions.soapOutput.title}
          </Typography>

          <Stack spacing={3}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              spacing={2}
            >
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                  {
                    settingDescriptions.soapOutput.items.is_enrich_subjective
                      .title
                  }
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {
                    settingDescriptions.soapOutput.items.is_enrich_subjective
                      .description
                  }
                </Typography>
              </Box>
              <Switch
                color="primary"
                checked={customSetting.is_enrich_subjective || false}
                onChange={() =>
                  updateSetting(
                    'is_enrich_subjective',
                    !customSetting.is_enrich_subjective
                  )
                }
              />
            </Stack>

            <Divider />

            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              spacing={2}
            >
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                  {
                    settingDescriptions.soapOutput.items.should_enhance_soap
                      .title
                  }
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {
                    settingDescriptions.soapOutput.items.should_enhance_soap
                      .description
                  }
                </Typography>
              </Box>
              <Switch
                color="primary"
                checked={customSetting.should_enhance_soap || false}
                onChange={() =>
                  updateSetting(
                    'should_enhance_soap',
                    !customSetting.should_enhance_soap
                  )
                }
              />
            </Stack>

            <Divider />

            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              spacing={2}
            >
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                  {settingDescriptions.soapOutput.items.is_short_ep.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {settingDescriptions.soapOutput.items.is_short_ep.description}
                </Typography>
              </Box>
              <Switch
                color="primary"
                checked={customSetting.is_short_ep ?? false}
                onChange={() =>
                  updateSetting('is_short_ep', !customSetting.is_short_ep)
                }
              />
            </Stack>

            <Divider />

            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              spacing={2}
            >
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                  {settingDescriptions.soapOutput.items.use_soap_template.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {
                    settingDescriptions.soapOutput.items.use_soap_template
                      .description
                  }
                </Typography>
              </Box>
              <Switch
                color="primary"
                checked={customSetting.use_soap_template ?? false}
                onChange={() =>
                  updateSetting(
                    'use_soap_template',
                    !customSetting.use_soap_template
                  )
                }
              />
            </Stack>
          </Stack>
        </Card>

        {/* SOAPデフォルト選択セクション */}
        <Card sx={{ p: 3 }}>
          <Typography
            variant="subtitle1"
            sx={{
              color: colorTokens.blue[700],
              mb: 2,
            }}
          >
            {settingDescriptions.soapDefaultSelection.title}
          </Typography>

          <Stack spacing={3}>
            {Object.entries(settingDescriptions.soapDefaultSelection.items).map(
              ([key, item], index, arr) => (
                <React.Fragment key={key}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    spacing={2}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                        {item.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {item.description}
                      </Typography>
                    </Box>
                    <Switch
                      color="primary"
                      checked={
                        (customSetting[key as keyof Setting] as boolean) ?? true
                      }
                      onChange={() =>
                        updateSetting(
                          key as keyof Setting,
                          !(
                            (customSetting[key as keyof Setting] as boolean) ??
                            true
                          )
                        )
                      }
                    />
                  </Stack>
                  {index < arr.length - 1 && <Divider />}
                </React.Fragment>
              )
            )}
          </Stack>
        </Card>

        {/* 表示設定セクション */}
        <Card sx={{ p: 3 }}>
          <Typography
            variant="subtitle1"
            sx={{
              color: colorTokens.blue[700],
              mb: 2,
            }}
          >
            {settingDescriptions.displaySettings.title}
          </Typography>

          <Stack spacing={3}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              spacing={2}
            >
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                  {settingDescriptions.displaySettings.items.display_days.title}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ whiteSpace: 'pre-line' }}
                >
                  {
                    settingDescriptions.displaySettings.items.display_days
                      .description
                  }
                </Typography>
              </Box>
              <FormControl sx={{ minWidth: 120 }}>
                <Select
                  value={customSetting.ui_config?.display_days ?? ''}
                  onChange={(e) =>
                    updateUIConfigMutation.mutate({
                      key: 'display_days',
                      value: e.target.value as number,
                    })
                  }
                  size="small"
                  sx={{ backgroundColor: 'background.paper' }}
                  displayEmpty
                >
                  <MenuItem value="" disabled>
                    読込中
                  </MenuItem>
                  <MenuItem value={1}>1日</MenuItem>
                  <MenuItem value={2}>2日</MenuItem>
                  <MenuItem value={3}>3日</MenuItem>
                  <MenuItem value={4}>4日</MenuItem>
                  <MenuItem value={5}>5日</MenuItem>
                  <MenuItem value={6}>6日</MenuItem>
                  <MenuItem value={7}>7日</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </Stack>
        </Card>
      </Stack>
    </PortalLayout>
  )
}

export default SoapSettingsPage
